// =============================================================================
// CENTRALISED PER-ROUTE SEO DATA
// =============================================================================
// Single source of truth for every page's <title>, primary H1 and meta
// description. Derived from the brand owner's exact-text table.
//
// Conventions:
//   - title:     exact <title> for that route (already includes the brand)
//   - h1:        the on-page primary H1 (must contain the primary keyword)
//   - meta:      the meta description (150-160 char range)
//   - noindex:   true for utility / thank-you / booking routes
//
// Notes:
//   - The bakery disambiguation lives in Organization JSON-LD + footer copy,
//     not here.
//   - Routes not present in this map fall back to homepage SEO (rare; mainly
//     covered by parametric routes that build their own SEO from data).
// =============================================================================

import { SITE } from "@/lib/siteData";

const BRAND = "Pie in the Sky Productions";

export const ROUTE_SEO = {
  "/": {
    title: `Videographer Cape Town & Video Production | ${BRAND}`,
    h1: "Videographer & Video Production in Cape Town",
    meta: "Pie in the Sky Productions is a Cape Town videographer and video production company - commercials, brand films and corporate video that captivate and convert.",
  },
  "/about": {
    title: `About Our Film & Video Team | ${BRAND}`,
    h1: "About Pie in the Sky Productions",
    meta: "Meet the Cape Town film crew behind Pie in the Sky Productions - directors, cinematographers and editors creating cinematic video for brands across South Africa.",
  },
  "/services": {
    title: `Video Production Services in Cape Town | ${BRAND}`,
    h1: "Video Production Services in Cape Town",
    meta: "Full-service video production in Cape Town: commercials, brand films, corporate video, documentaries, post-production and photography. See all services.",
  },
  "/portfolio": {
    title: `Video Production Portfolio | ${BRAND}`,
    h1: "Our Video Production Portfolio",
    meta: "Watch our Cape Town video production portfolio - commercials, brand films and corporate video produced by Pie in the Sky Productions.",
  },
  "/case-studies": {
    title: `Video Case Studies | ${BRAND}`,
    h1: "Video Production Case Studies",
    meta: "Real results from our Cape Town video projects. See how Pie in the Sky Productions helped brands tell their story on screen.",
  },
  "/crew": {
    title: `Cape Town Film Crew | ${BRAND}`,
    h1: "Our Cape Town Film Crew",
    meta: "Hire an experienced Cape Town film crew - directors, DOPs, gaffers and more - from Pie in the Sky Productions for your next shoot.",
  },
  "/equipment": {
    title: `Film & Video Equipment | ${BRAND}`,
    h1: "Film & Video Equipment",
    meta: "Cinema cameras, lenses, lighting and grip for productions in Cape Town. Explore the kit behind Pie in the Sky Productions.",
  },
  "/photography": {
    title: `Commercial Photography Cape Town | ${BRAND}`,
    h1: "Commercial Photography in Cape Town",
    meta: "Professional commercial and brand photography in Cape Town by Pie in the Sky Productions, alongside our video production services.",
  },
  "/industries": {
    title: `Video Production by Industry | ${BRAND}`,
    h1: "Video Production by Industry",
    meta: "Industry-specific video production in Cape Town - from hotels and construction to medical and marketing agencies. See how we help your sector.",
  },
  "/process": {
    title: `Our Video Production Process | ${BRAND}`,
    h1: "Our Video Production Process",
    meta: "From brief to final cut: see how Pie in the Sky Productions plans, shoots and delivers video projects in Cape Town.",
  },
  "/pricing": {
    title: `Video Production Pricing | ${BRAND}`,
    h1: "Video Production Pricing",
    meta: "Transparent video production pricing for Cape Town projects. Get an idea of costs for commercials, brand films and corporate video.",
  },
  "/contact": {
    title: `Contact Our Cape Town Video Team | ${BRAND}`,
    h1: "Contact Pie in the Sky Productions",
    meta: "Get in touch with Pie in the Sky Productions, a Cape Town film & video production company. Email info@pieinthesky.co.za to start your project.",
  },
  "/blog": {
    title: `Video Production Blog | ${BRAND}`,
    h1: "Video Production Blog",
    meta: "Tips and insights on video production, filming and videography in Cape Town from the Pie in the Sky Productions team.",
  },
  "/careers": {
    title: `Careers in Film & Video | ${BRAND}`,
    h1: "Careers at Pie in the Sky Productions",
    meta: "Join our Cape Town film & video production team. See current openings and crew opportunities at Pie in the Sky Productions.",
  },
  "/press": {
    title: `Press & Media | ${BRAND}`,
    h1: "Press & Media",
    meta: "Press resources and media coverage for Pie in the Sky Productions, a Cape Town film & video production company.",
  },
  "/references": {
    title: `Client References | ${BRAND}`,
    h1: "Client References",
    meta: "What our clients say about working with Pie in the Sky Productions on video projects in Cape Town and beyond.",
  },
  "/privacy": {
    title: `Privacy Policy | ${BRAND}`,
    h1: "Privacy Policy",
    meta: "Privacy policy for Pie in the Sky Productions, a Cape Town film & video production company.",
  },
  "/working-agreement": {
    title: `Working Agreement | ${BRAND}`,
    h1: "Working Agreement",
    meta: "Our standard working agreement and terms for video production projects with Pie in the Sky Productions.",
  },

  // ---- /services/* (slugs as registered in App.js + services data) -----------
  "/services/video-production-company-cape-town": {
    title: `Video Production Company in Cape Town | ${BRAND}`,
    h1: "Video Production Company in Cape Town",
    meta: "Award-worthy video production company in Cape Town. Pie in the Sky Productions delivers commercials, brand films and corporate video that convert.",
  },
  "/services/videographer-cape-town": {
    title: `Hire a Videographer in Cape Town | ${BRAND}`,
    h1: "Hire a Videographer in Cape Town",
    meta: "Hiring a videographer in Cape Town? See what's included, typical rates and how Pie in the Sky Productions plans, shoots and edits your video.",
  },
  "/services/video-production-agency": {
    title: `Video Production Agency | ${BRAND}`,
    h1: "Video Production Agency",
    meta: "A full-service video production agency in Cape Town handling everything from concept to final cut. Work with Pie in the Sky Productions.",
  },
  "/services/commercials": {
    title: `TV & Online Commercial Production | ${BRAND}`,
    h1: "Commercial Video Production",
    meta: "Cape Town commercial production for TV and online. Pie in the Sky Productions creates ads that grab attention and drive results.",
  },
  "/services/brand-films": {
    title: `Brand Film Production | ${BRAND}`,
    h1: "Brand Film Production",
    meta: "Tell your brand story on screen. Pie in the Sky Productions creates cinematic brand films for businesses in Cape Town and across South Africa.",
  },
  "/services/corporate-video": {
    title: `Corporate Video Production | ${BRAND}`,
    h1: "Corporate Video Production",
    meta: "Professional corporate video production in Cape Town - internal comms, training, profiles and more - by Pie in the Sky Productions.",
  },
  "/services/documentary-filmmaking": {
    title: `Documentary Filmmaking | ${BRAND}`,
    h1: "Documentary Filmmaking in Cape Town",
    meta: "Compelling documentary filmmaking in Cape Town. Pie in the Sky Productions tells true stories with cinematic craft.",
  },
  "/services/post-production": {
    title: `Video Post-Production & Editing | ${BRAND}`,
    h1: "Video Post-Production & Editing",
    meta: "Editing, colour, sound and VFX. Pie in the Sky Productions offers full post-production for video projects in Cape Town.",
  },
  "/services/production-crew-support": {
    title: `Production Crew Support | ${BRAND}`,
    h1: "Production Crew Support",
    meta: "Local fixer and crew support for productions shooting in Cape Town. Pie in the Sky Productions supplies experienced film crew.",
  },
  "/services/models-talent": {
    title: `Models & Talent | ${BRAND}`,
    h1: "Models & Talent for Video",
    meta: "Casting, models and on-screen talent for video shoots in Cape Town, arranged by Pie in the Sky Productions.",
  },
  "/services/ai-video-editing": {
    title: `AI Video Editing | ${BRAND}`,
    h1: "AI-Assisted Video Editing",
    meta: "Faster, smarter edits with AI-assisted video editing from Pie in the Sky Productions, a Cape Town video production company.",
  },

  // ---- /for/* niche landing pages --------------------------------------------
  "/for/hotels-and-lodges": {
    title: `Video for Hotels & Lodges | ${BRAND}`,
    h1: "Video Production for Hotels & Lodges",
    meta: "Showcase your property with cinematic hospitality video. Pie in the Sky Productions films hotels and lodges across the Cape and beyond.",
  },
  "/for/construction-services": {
    title: `Video for Construction | ${BRAND}`,
    h1: "Video Production for Construction",
    meta: "Document projects and market your firm with construction video from Pie in the Sky Productions, Cape Town.",
  },
  "/for/marketing-agencies": {
    title: `Video for Marketing Agencies | ${BRAND}`,
    h1: "Video Production for Marketing Agencies",
    meta: "A reliable Cape Town production partner for agencies. White-label video production from Pie in the Sky Productions.",
  },
  "/for/medical-dental-pharma": {
    title: `Video for Medical, Dental & Pharma | ${BRAND}`,
    h1: "Video for Medical, Dental & Pharma",
    meta: "Clear, compliant healthcare video. Pie in the Sky Productions produces medical, dental and pharma video in Cape Town.",
  },

  // ---- Utility / non-indexable -----------------------------------------------
  "/free-consultation":                       { noindex: true, title: `Book a Free Consultation | ${BRAND}`, h1: "Book a Free Consultation", meta: "Schedule a free consultation with the Pie in the Sky Productions team to scope your next video project." },
  "/booking-calendar/free-consultation":      { noindex: true, title: `Free Consultation | ${BRAND}`,        h1: "Free Consultation",        meta: "Pick a time for a free consultation with Pie in the Sky Productions." },
  "/booking-calendar/video-production":       { noindex: true, title: `Book Video Production | ${BRAND}`,   h1: "Book a Video Production Call", meta: "Schedule a video production scoping call with Pie in the Sky Productions." },
  "/booking-calendar/pre-production":         { noindex: true, title: `Book Pre-Production | ${BRAND}`,     h1: "Book a Pre-Production Call",   meta: "Schedule a pre-production planning call with Pie in the Sky Productions." },
  "/booking-calendar/post-production":        { noindex: true, title: `Book Post-Production | ${BRAND}`,    h1: "Book a Post-Production Call",  meta: "Schedule a post-production review with Pie in the Sky Productions." },
  "/booking-calendar/photography":            { noindex: true, title: `Book Photography | ${BRAND}`,        h1: "Book a Photography Call",      meta: "Schedule a photography brief call with Pie in the Sky Productions." },
  "/booking-calendar/hair-makeup":            { noindex: true, title: `Book Hair & Makeup | ${BRAND}`,      h1: "Book Hair & Makeup",           meta: "Schedule hair & makeup for an upcoming Pie in the Sky Productions shoot." },
  "/booking-calendar/vehicle-rental-1":       { noindex: true, title: `Book Vehicle Rental | ${BRAND}`,     h1: "Book a Vehicle Rental",        meta: "Reserve a production vehicle for an upcoming Pie in the Sky Productions shoot." },

  // ---- Legacy redirect aliases (noindex - prerendered so crawlers see "noindex" but reach canonical via JS Navigate / canonical link) ----
  "/photos":                                  { noindex: true, redirectTo: "/photography",                                          title: `Photography | ${BRAND}`,              h1: "Photography",                meta: "Redirect to photography portfolio." },
  "/photo-portfolio":                         { noindex: true, redirectTo: "/photography",                                          title: `Photography | ${BRAND}`,              h1: "Photography",                meta: "Redirect to photography portfolio." },
  "/photographer-cape-town":                  { noindex: true, redirectTo: "/photography",                                          title: `Photographer Cape Town | ${BRAND}`,   h1: "Photographer Cape Town",     meta: "Redirect to photography portfolio." },
  "/photographer-cape-town-portfolio":        { noindex: true, redirectTo: "/photography",                                          title: `Photographer Portfolio | ${BRAND}`,   h1: "Photographer Portfolio",     meta: "Redirect to photography portfolio." },
  "/privacy-policy":                          { noindex: true, redirectTo: "/privacy",                                              title: `Privacy Policy | ${BRAND}`,           h1: "Privacy Policy",             meta: "Redirect to privacy policy." },
  "/terms":                                   { noindex: true, redirectTo: "/working-agreement",                                    title: `Terms | ${BRAND}`,                    h1: "Terms",                      meta: "Redirect to working agreement." },
  "/terms-of-service":                        { noindex: true, redirectTo: "/working-agreement",                                    title: `Terms of Service | ${BRAND}`,         h1: "Terms of Service",           meta: "Redirect to working agreement." },
  "/testimonials":                            { noindex: true, redirectTo: "/references",                                           title: `Testimonials | ${BRAND}`,             h1: "Testimonials",               meta: "Redirect to client references." },
  "/videographer-cape-town-prices":           { noindex: true, redirectTo: "/pricing",                                              title: `Pricing | ${BRAND}`,                  h1: "Pricing",                    meta: "Redirect to pricing page." },
  "/plans-and-pricing":                       { noindex: true, redirectTo: "/pricing",                                              title: `Pricing | ${BRAND}`,                  h1: "Pricing",                    meta: "Redirect to pricing page." },
  "/videographer-services-cape-town":         { noindex: true, redirectTo: "/services",                                             title: `Services | ${BRAND}`,                 h1: "Services",                   meta: "Redirect to services hub." },
  "/contact-us-pie-in-the-sky-productions":   { noindex: true, redirectTo: "/contact",                                              title: `Contact | ${BRAND}`,                  h1: "Contact",                    meta: "Redirect to contact page." },
  "/cape-town-video-production-company-portfolio": { noindex: true, redirectTo: "/portfolio",                                       title: `Portfolio | ${BRAND}`,                h1: "Portfolio",                  meta: "Redirect to portfolio page." },
};

/**
 * Lookup helper - returns SEO config for a route, with safe fallback to home.
 */
export const seoFor = (path) => ROUTE_SEO[path] || ROUTE_SEO["/"];

/**
 * Generate fallback SEO for an unlisted /post/ blog slug (per brief).
 */
export const seoForPost = (slug, title, excerpt) => {
  const cleanTitle = (title || slug.replace(/-/g, " ")).replace(/\s+/g, " ").trim();
  // 150-160 char meta description
  const baseExcerpt = (excerpt || "Insights on video production, filming and videography in Cape Town from Pie in the Sky Productions.").replace(/\s+/g, " ").trim();
  const meta = baseExcerpt.length <= 160 ? baseExcerpt : baseExcerpt.slice(0, 157).replace(/[\s,]+$/, "") + "...";
  return {
    title: `${cleanTitle} | ${BRAND}`,
    h1: cleanTitle,
    meta,
  };
};

export const BRAND_NAME = BRAND;
export { SITE };
